import { useMutation } from "@tanstack/react-query";
import ApiClient from "../../lib/api-client";
import { CartItemDto } from "../../lib/api-client-generated-types";
import { queryUtils } from "../../lib/query-client";
import useCustomTranslation from "../../hooks/use-custom-translation";
import { useToast } from "../use-toast";
import useCart from "./use-cart";

export default function useUpdateCart() {
  const { t } = useCustomTranslation();

  const { toast } = useToast();

  const { cart } = useCart();

  const {
    mutate: updateCart,
    isPending: isUpdatingCart,
    isError: isUpdateCartError,
    error: updateCartError,
    isSuccess: isUpdateCartSuccess,
  } = useMutation({
    mutationFn: (items: CartItemDto[]) => ApiClient.updateUserCart({ items }),

    onSettled: () => {
      queryUtils.cart.invalidate();
    },
    onSuccess: () => {
      toast({
        title: t("cart.updated"),
        description: t("cart.updatedDescription"),
      });
    },
    onError: () => {
      toast({
        title: t("cart.updateError"),
        description: t("cart.updateErrorDescription"),
      });
    },
  });

  const incrementCartItem = (productId: number) => {
    if (!cart) return;

    const updatedItems = cart.map((item) => {
      if (item.productId === productId) {
        return { ...item, quantity: item.quantity + 1 };
      }
      return item;
    });

    updateCart(updatedItems);
  };

  const decrementCartItem = (productId: number) => {
    if (!cart) return;

    const updatedItems = cart.map((item) => {
      if (item.productId === productId && item.quantity > 1) {
        return { ...item, quantity: item.quantity - 1 };
      }

      if (item.productId === productId && item.quantity === 1) {
        return null;
      }

      return item;
    });

    updateCart(updatedItems.filter((item) => item !== null));
  };

  const removeCartItem = (productId: number) => {
    if (!cart) return;

    const updatedItems = cart.filter((item) => item.productId !== productId);

    updateCart(updatedItems);
  };

  const addCartItem = (productId: number, aiInterviewTemplateId?: number) => {
    if (!cart) {
      updateCart([{ productId, quantity: 1, aiInterviewTemplateId }]);
      return;
    }

    const existingItem = cart.find((item) => item.productId === productId);

    if (existingItem) {
      const updatedItems = cart.map((item) => {
        if (item.productId === productId) {
          return { ...item, quantity: item.quantity + 1 };
        }
        return item;
      });
      updateCart(updatedItems);
    } else {
      updateCart([...cart, { productId, quantity: 1, aiInterviewTemplateId }]);
    }
  };

  return {
    updateCart,
    incrementCartItem,
    decrementCartItem,
    removeCartItem,
    addCartItem,
    isUpdatingCart,
    isUpdateCartError,
    updateCartError,
    isUpdateCartSuccess,
  };
}
